import React, { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Box, Typography, Button, Alert, CircularProgress } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { supabase } from "../supabaseClient";
import { logRegistrationEvent } from "../utils/funnelAnalytics";
import { fadeUp } from "./ui/motion";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;

const RegisterSuccess = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const noPayment = location.state?.noPayment === true;

  const [status, setStatus] = useState(
    noPayment ? "done" : sessionId ? "pending" : "done"
  );

  useEffect(() => {
    logRegistrationEvent("reg_success_page_view", {
      has_stripe_session: Boolean(sessionId),
      no_payment_flow: Boolean(noPayment),
    });
  }, [sessionId, noPayment]);

  useEffect(() => {
    if (noPayment || !sessionId || status !== "pending") return;

    const check = async () => {
      const { data } = await supabase
        .from("league_teams")
        .select("id")
        .eq("stripe_session_id", sessionId)
        .maybeSingle();
      if (data?.id) {
        setStatus("confirmed");
        return true;
      }
      return false;
    };

    let cancelled = false;
    const start = Date.now();

    const run = async () => {
      if (await check()) return;
      while (!cancelled && Date.now() - start < POLL_TIMEOUT_MS) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        if (cancelled) return;
        if (await check()) return;
      }
      if (!cancelled) setStatus("failed");
    };

    run();
    return () => { cancelled = true; };
  }, [noPayment, sessionId, status]);

  const shell = (icon, title, body, extra) => (
    <Box sx={{ minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center", px: 2, py: 6 }}>
      <Box
        sx={{
          width: "100%",
          maxWidth: 480,
          bgcolor: "#fff",
          border: "1px solid #ececec",
          borderRadius: 5,
          p: { xs: 3, sm: 4 },
          textAlign: "center",
          boxShadow: "0 2px 4px rgba(16,24,40,0.06), 0 18px 40px rgba(16,24,40,0.08)",
          ...fadeUp(0),
        }}
      >
        {icon}
        <Typography component="h1" sx={{ fontSize: { xs: "1.5rem", sm: "1.8rem" }, fontWeight: 900, letterSpacing: "-0.02em", mt: 2 }}>
          {title}
        </Typography>
        <Typography sx={{ color: "#666", mt: 1, lineHeight: 1.55 }}>{body}</Typography>
        {extra}
      </Box>
    </Box>
  );

  const badge = (bg, child) => (
    <Box sx={{ width: 76, height: 76, mx: "auto", borderRadius: "50%", bgcolor: bg, display: "flex", alignItems: "center", justifyContent: "center" }}>{child}</Box>
  );

  if (status === "pending") {
    return shell(
      badge("#f4f4f5", <CircularProgress size={34} sx={{ color: "#111" }} />),
      "Confirming your registration",
      "Payment received. We're setting up your team — this usually takes a few seconds."
    );
  }

  if (status === "failed") {
    return shell(
      badge("#fee2e2", <ErrorOutlineIcon sx={{ fontSize: 40, color: "#b91c1c" }} />),
      "Something went wrong",
      "Your payment went through, but we couldn't confirm your team was created.",
      <>
        <Alert severity="warning" sx={{ mt: 2.5, textAlign: "left", borderRadius: 3 }}>
          Please contact us at <strong>registrations@hadafsports.com</strong> with your email and we'll sort it out.
        </Alert>
        <Button component={Link} to="/" variant="contained" sx={{ mt: 3, bgcolor: "#0a0a0a", borderRadius: 999, px: 3, "&:hover": { bgcolor: "#222" } }}>
          Back to Home
        </Button>
      </>
    );
  }

  const steps = noPayment
    ? ["Registration received", "We'll email you with next steps", "Schedule is published on Fixtures"]
    : ["Payment confirmed", "Your registration appears on Home", "Schedule is published on Fixtures"];

  return shell(
    badge(
      "#dcfce7",
      <CheckRoundedIcon
        sx={{
          fontSize: 44,
          color: "#15803d",
          "@keyframes hslPop": { "0%": { transform: "scale(0.4)", opacity: 0 }, "70%": { transform: "scale(1.12)" }, "100%": { transform: "scale(1)", opacity: 1 } },
          animation: "hslPop 480ms cubic-bezier(.2,.8,.2,1) both",
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      />
    ),
    noPayment ? "Registration received" : "You're registered",
    noPayment
      ? "Thank you for registering. We will reach out to you at the email you used to register."
      : "Thank you for registering. Your team will be available on Home.",
    <>
      <Box sx={{ mt: 3, textAlign: "left", display: "flex", flexDirection: "column", gap: 1.25 }}>
        {steps.map((step, i) => (
          <Box key={step} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 26, height: 26, borderRadius: "50%", flexShrink: 0, fontSize: "0.78rem", fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: i === 0 ? "#15803d" : "#f4f4f5", color: i === 0 ? "#fff" : "#666" }}>
              {i === 0 ? <CheckRoundedIcon sx={{ fontSize: 16 }} /> : i + 1}
            </Box>
            <Typography sx={{ fontWeight: i === 0 ? 800 : 600, color: i === 0 ? "#111" : "#555", fontSize: "0.92rem" }}>{step}</Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: 1, mt: 3.5, justifyContent: "center", flexWrap: "wrap" }}>
        <Button component={Link} to="/" variant="contained" sx={{ bgcolor: "#0a0a0a", borderRadius: 999, px: 3, fontWeight: 800, "&:hover": { bgcolor: "#222" } }}>
          Go to Home
        </Button>
        <Button component={Link} to="/schedule" sx={{ borderRadius: 999, px: 2.5, fontWeight: 700, color: "#111", border: "1px solid #e4e4e7" }}>
          See fixtures
        </Button>
      </Box>
    </>
  );
};

export default RegisterSuccess;
